import { AggregateItem } from "~/modules/asset_timeseries_v4/types";
import { VisualizationCard } from "../VisualizationCard";
import { ColumnSpanConfig } from "~/types";
import {
    AggregateFunctions,
    AggregateQueryInterval,
    AggregateQueryMode,
    ApiQueryEndpoints,
    Filter,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import { useApiQuery } from "~/modules/filters/components/useApiQuery";
import { DEFAULT_TOKEN_TIMESERIES_QUERY } from "~/modules/token_timeseries/consts";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { MEASURE_SLUGS } from "~/consts";
import { useState } from "react";
import { SelectInput } from "~/components/SelectInput";
import { FadeIn } from "~/components/FadeIn";
import { LogoLoadingSpinner } from "~/components/LoadingSpinner";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";
import dynamic from "next/dynamic";
import { WindowResizeObserver } from "~/components/WindowResizeObserver";
import { SelectLoginGate } from "~/components/SelectInput/SelectLoginGate";
import { useUser } from "~/modules/user/hooks/useUser";

const ScatterChart = dynamic(
    () => import("../ScatterChart/component").then((mod) => mod.ScatterChart),
    {
        ssr: false,
    },
);

// // // //

const DEFAULT_GROUP_BY_VALUES = [
    TokenTimeseriesGroupBy.asset,
    TokenTimeseriesGroupBy.token,
    TokenTimeseriesGroupBy.protocol,
    TokenTimeseriesGroupBy.manager,
    TokenTimeseriesGroupBy.parentNetwork,
    TokenTimeseriesGroupBy.assetClass,
    TokenTimeseriesGroupBy.jurisdictionCountry,
    TokenTimeseriesGroupBy.peggedAsset,
];

const DEFAULT_GROUP_BY_LABELS: Record<TokenTimeseriesGroupBy, string> = {
    [TokenTimeseriesGroupBy.asset]: "Asset",
    [TokenTimeseriesGroupBy.token]: "Token",
    [TokenTimeseriesGroupBy.protocol]: "Platform",
    [TokenTimeseriesGroupBy.manager]: "Manager",
    [TokenTimeseriesGroupBy.parentNetwork]: "Network",
    [TokenTimeseriesGroupBy.assetClass]: "Asset Class",
    [TokenTimeseriesGroupBy.jurisdictionCountry]: "Jurisdiction Country",
    [TokenTimeseriesGroupBy.peggedAsset]: "Pegged Asset",
} as Record<TokenTimeseriesGroupBy, string>;

export interface Props {
    title: string;
    colSpans: ColumnSpanConfig;
    filters: Filter[];
    totalValueMeasureSlug: string;
    totalValueMeasureName: string;
    defaultGroupBy: TokenTimeseriesGroupBy;
    allowedGroupByValues?: TokenTimeseriesGroupBy[];
    groupByLabelOverrides?: Partial<Record<TokenTimeseriesGroupBy, string>>;
}

export function ScatterChartCard(props: Props) {
    const {
        title,
        colSpans,
        filters,
        defaultGroupBy,
        totalValueMeasureSlug,
        totalValueMeasureName,
        allowedGroupByValues = DEFAULT_GROUP_BY_VALUES,
        groupByLabelOverrides,
    } = props;

    const { isLoggedIn } = useUser();

    const [groupBy, setGroupBy] =
        useState<TokenTimeseriesGroupBy>(defaultGroupBy);

    const marketCapReq = useApiQuery({
        endpoint: ApiQueryEndpoints.TokenAggregatesV4,
        query: {
            ...DEFAULT_TOKEN_TIMESERIES_QUERY,
            aggregate: {
                aggregateFunction: AggregateFunctions.Sum,
                groupBy: groupBy,
                mode: AggregateQueryMode.stock,
                // groupLimit: 20,
            },
            filter: {
                operator: FilterOperators.And,
                filters: [
                    {
                        field: TokenTimeseriesColumns.measureSlug,
                        operator: FilterOperators.Equals,
                        value: totalValueMeasureSlug,
                    },
                    ...filters,
                ],
            },
        },
    });

    const volumeReq = useApiQuery({
        endpoint: ApiQueryEndpoints.TokenAggregatesV4,
        query: {
            ...DEFAULT_TOKEN_TIMESERIES_QUERY,
            aggregate: {
                aggregateFunction: AggregateFunctions.Sum,
                groupBy: groupBy,
                mode: AggregateQueryMode.stock,
                interval: AggregateQueryInterval.month,
                // groupLimit: 20,
            },
            filter: {
                operator: FilterOperators.And,
                filters: [
                    {
                        field: TokenTimeseriesColumns.measureSlug,
                        operator: FilterOperators.Equals,
                        value: MEASURE_SLUGS.daily_transfer_volume_dollar,
                    },
                    ...filters,
                ],
            },
        },
    });

    const isLoading =
        marketCapReq.isLoading ||
        // activeAddressesReq.isLoading ||
        volumeReq.isLoading;

    const marketCapData: AggregateItem[] = (marketCapReq.data?.results ??
        []) as AggregateItem[];

    const volumeData: AggregateItem[] = (volumeReq.data?.results ??
        []) as AggregateItem[];

    // const activeAddressesData: AggregateItem[] = (activeAddressesReq.data
    //     ?.results ?? []) as AggregateItem[];

    const sortedMarketCapData = sortItemsByProperty({
        items: marketCapData,
        field: "value",
        direction: SortDirections.desc,
    }).slice(0, 20);

    return (
        <VisualizationCard
            title={title}
            infoTooltip={`30-day trading volume relative to ${totalValueMeasureName.toLowerCase()}`}
            watermark={isLoading ? "none" : "center-40pct"}
            wrapperClassNameOverride=""
            showTimestamp={false}
            colSpans={colSpans}
            customControls={
                <div className="flex items-center gap-2">
                    <p className="text-sm text-foreground whitespace-nowrap select-none">
                        Group By
                    </p>
                    <SelectLoginGate isLoggedIn={isLoggedIn}>
                        <SelectInput
                            value={groupBy}
                            disabled={!isLoggedIn}
                            onChange={(updatedValue) =>
                                setGroupBy(
                                    updatedValue as TokenTimeseriesGroupBy,
                                )
                            }
                            size="sm"
                            options={allowedGroupByValues.map((opt) => ({
                                value: opt,
                                content: {
                                    label:
                                        groupByLabelOverrides?.[opt] ??
                                        DEFAULT_GROUP_BY_LABELS[opt] ??
                                        opt,
                                },
                            }))}
                        />
                    </SelectLoginGate>
                </div>
            }
        >
            {isLoading && (
                <FadeIn>
                    <div
                        className="flex items-center justify-center w-full"
                        style={{
                            height: `${390}px`,
                        }}
                    >
                        <LogoLoadingSpinner size={75} />
                    </div>
                </FadeIn>
            )}
            {!isLoading && (
                <WindowResizeObserver
                    delay={1200}
                    defaultPlaceholderHeight={390}
                >
                    <ScatterChart
                        xItems={volumeData.map((item) => ({
                            ...item,
                            measure: {
                                ...item.measure,
                                name: "30D Transfer Volume",
                            },
                        }))}
                        yItems={sortedMarketCapData.map((item) => ({
                            ...item,
                            measure: {
                                ...item.measure,
                                name: totalValueMeasureName,
                            },
                        }))}
                        // zItems={activeAddressesData}
                    />
                </WindowResizeObserver>
            )}
        </VisualizationCard>
    );
}
